// Thin wrapper around the bot's HTTP API. In dev, /api/* is proxied to
// http://localhost:8787 (see vite.config.ts). In prod, the admin SPA
// is hosted on admin.deadunlock.gg and calls https://bot.deadunlock.gg
// directly with CORS allowance.
//
// Auth: every request attaches the current Supabase session JWT as a
// Bearer token. The bot's requireAdmin middleware verifies it with
// SUPABASE_JWT_SECRET and checks the email against ADMIN_EMAILS.
import { getAccessToken } from './supabase';

const BOT_BASE = (import.meta.env.VITE_BOT_URL as string | undefined) ?? '';
// In dev with no VITE_BOT_URL, fetches go through Vite's proxy at /api/*.
// In prod, set VITE_BOT_URL=https://bot.deadunlock.gg.

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${BOT_BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

function postJson<T>(path: string, body: unknown): Promise<T> {
  return call<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

function del<T>(path: string): Promise<T> {
  return call<T>(path, { method: 'DELETE' });
}

// ----- Types mirror the bot's HTTP responses -----

export interface BotState {
  loggedOn: boolean;
  gcReady: boolean;
  steamId: string | null;
  accountName: string | null;
  startedAt: number;
  lastReconnectAt: number | null;
  reconnects: number;
  errors: { ts: number; msg: string }[];
}

export interface PendingGuard { needed: boolean; domain: string | null }

export interface BotSummary {
  id: string;
  label: string;
  username: string;
  hasSharedSecret: boolean;
  state: BotState;
  pendingGuard: PendingGuard;
  friendsCount: number;
  callsCount: number;
  gcRate: { callsInLastMinute: number; max: number };
  createdAt: number;
}

export interface FriendEntry {
  steamId: string;
  accountId: number;
  personaName: string | null;
  friendedAt: number;
  source: 'incoming' | 'preexisting';
}

export interface CallLogEntry {
  ts: number;
  kind: 'history' | 'metadata' | 'other';
  target: string;
  durationMs: number;
  ok: boolean;
  error?: string;
  resultSummary?: string;
}

// ----- API surface -----

export const api = {
  health: () => call<{ ok: boolean; botCount: number; botsLoggedOn: number; botsGcReady: number; totalFriends: number }>('/api/health'),

  // pool
  listBots: () => call<{ bots: BotSummary[] }>('/api/admin/bots'),
  addBot: (b: { label?: string; username: string; password: string; sharedSecret?: string }) =>
    postJson<{ bot: BotSummary }>('/api/admin/bots', b),
  removeBot: (id: string) => del<{ ok: boolean }>(`/api/admin/bots/${id}`),

  // per-bot
  bot: (id: string) => ({
    state:    () => call<{ summary: BotSummary; bot: BotState; pendingGuard: PendingGuard }>(`/api/bots/${id}/state`),
    friends:  () => call<{ friends: FriendEntry[] }>(`/api/bots/${id}/friends`),
    calls:    (limit = 50) => call<{ calls: CallLogEntry[] }>(`/api/bots/${id}/calls?limit=${limit}`),
    login:    (sharedSecret?: string) =>
      postJson<{ status: 'logged_on' | 'needs_guard' | 'error'; error?: string; domain?: string | null }>(
        `/api/bots/${id}/login`,
        sharedSecret ? { sharedSecret } : {},
      ),
    submitGuard: (code: string) => postJson<{ status: 'submitted' | 'no_pending' }>(`/api/bots/${id}/login/guard`, { code }),
    logout:   () => postJson<{ ok: boolean }>(`/api/bots/${id}/logout`, {}),
    history:  (steamid: string) => call<{ matches: unknown[]; friended: boolean; result: number }>(`/api/bots/${id}/history?steamid=${encodeURIComponent(steamid)}`),
    metadata: (matchId: string) => call<Record<string, unknown>>(`/api/bots/${id}/metadata?match_id=${encodeURIComponent(matchId)}`),
  }),
};
